import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "ih-announcement-dismissed-ecoflow-v1";

export default function AnnouncementBar() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [email, setEmail] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // ignore
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("submitting");
    try {
      const body = new URLSearchParams();
      body.append("email", email.trim());
      body.append("brand", "EcoFlow");
      body.append("source", "Announcement Bar — EcoFlow Coming Soon");
      const res = await fetch("https://readdy.ai/api/form/d77qgkadqn9mib2tqc3g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (dismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-gray-950 border-b border-amber-500/20">
      {/* Subtle amber glow line at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between gap-3 py-2.5">

        {/* Left — icon + copy */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-6 h-6 flex items-center justify-center bg-amber-500/20 rounded-md shrink-0">
            <i className="ri-flashlight-line text-amber-400 text-sm"></i>
          </div>
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="hidden sm:inline-flex items-center gap-1 bg-amber-500/20 border border-amber-500/30 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
              <i className="ri-time-line text-[10px]"></i>
              Coming Soon
            </span>
            <p className="text-white text-xs font-semibold whitespace-nowrap">
              EcoFlow is coming to Iron Haven Supply
            </p>
            <p className="hidden md:block text-gray-500 text-xs">
              — DELTA &amp; RIVER series, authorized dealer pricing
            </p>
          </div>
        </div>

        {/* Center — inline form or success */}
        <div className="flex-1 flex justify-center max-w-sm mx-4 hidden sm:flex">
          {status === "success" ? (
            <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/25 rounded-lg px-3 py-1.5">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-check-line text-emerald-400 text-xs"></i>
              </div>
              <p className="text-emerald-300 text-xs font-semibold whitespace-nowrap">
                You&apos;re on the list — we&apos;ll email you at launch!
              </p>
            </div>
          ) : formOpen ? (
            <form
              data-readdy-form
              id="ecoflow-announcement-bar-notify"
              onSubmit={handleSubmit}
              className="flex items-center gap-2 w-full"
            >
              <input
                type="email"
                name="email"
                required
                autoFocus
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border border-white/20 text-white placeholder-gray-600 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-400/60 transition-colors min-w-0"
              />
              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                {status === "submitting" ? (
                  <>
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin text-xs"></i>
                    </div>
                    Sending
                  </>
                ) : (
                  <>
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-bell-line text-xs"></i>
                    </div>
                    Notify Me
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-400 transition-colors cursor-pointer shrink-0"
                aria-label="Close form"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold px-4 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-bell-line text-xs"></i>
              </div>
              Get Early Access
            </button>
          )}
        </div>

        {/* Right — learn more + dismiss */}
        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/authorized-brands"
            className="hidden lg:flex items-center gap-1 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            Learn more
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </Link>

          {/* Mobile CTA — only when form not open */}
          {!formOpen && status !== "success" && (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="sm:hidden flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-bell-line text-[10px]"></i>
              </div>
              Notify Me
            </button>
          )}

          <button
            type="button"
            onClick={handleDismiss}
            className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-300 transition-colors cursor-pointer rounded-md hover:bg-white/10 shrink-0"
            aria-label="Dismiss announcement"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>
      </div>

      {/* Mobile inline form — shown below the bar when open on small screens */}
      {formOpen && (
        <div className="sm:hidden border-t border-white/10 px-4 py-3">
          {status === "success" ? (
            <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/25 rounded-lg px-3 py-2">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-check-line text-emerald-400 text-xs"></i>
              </div>
              <p className="text-emerald-300 text-xs font-semibold">You&apos;re on the list — we&apos;ll email you at launch!</p>
            </div>
          ) : (
            <form
              data-readdy-form
              id="ecoflow-announcement-bar-mobile-notify"
              onSubmit={handleSubmit}
              className="flex gap-2"
            >
              <input
                type="email"
                name="email"
                required
                autoFocus
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border border-white/20 text-white placeholder-gray-600 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400/60 transition-colors"
              />
              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-xs font-bold px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                {status === "submitting" ? (
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin text-xs"></i>
                  </div>
                ) : "Notify Me"}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
